import React from 'react';
import { TableDataHeader } from '@block-explorer/ui';
import { TableRow } from '@make-software/cspr-design';
import AccountRewardListItem from './AccountRewardListItem';
import TabDataTable from '../../../components/tab-data-table/tab-data-table';
import { Trans, useTranslation } from 'react-i18next';
import { useCreateAccountsInfoGetter } from '../../../hooks';
import {
  useCurrencyRateAmount,
  useGetPaginatedAccountDelegatorRewards,
} from '../../../hooks/event-store-hooks';
import { useSelector } from 'react-redux';
import { RootState } from 'typesafe-actions';

export interface AccountRewardListProps {
  publicKey: string;
}

const AccountRewardList = ({ publicKey }: AccountRewardListProps) => {
  const { t } = useTranslation();
  const { selectedCurrency: currency } = useSelector(
    (state: RootState) => state.currencies
  );

  const { data, loading, error, paginationProps, sortingProps } =
    useGetPaginatedAccountDelegatorRewards({
      publicKey,
      includes: `rate(${currency.id}),validator_public_key`,
    });

  const { data: rateData, loading: loadingRateData } = useCurrencyRateAmount({
    currencyCode: currency.id.toString(),
  });

  const currencyRate = rateData?.data?.amount;

  const publicKeysList =
    data == null ? null : data.map((d) => d.validator_public_key);

  const { getAccountInfo, loading: loadingAccountsInfo } =
    useCreateAccountsInfoGetter(publicKeysList);

  return (
    <TabDataTable
      loading={
        data == null || loading || loadingAccountsInfo || loadingRateData
      }
      data={data}
      error={error}
      sortingProps={sortingProps}
      paginationProps={paginationProps}
      renderDataHeader={({ sortingProps }) => (
        <TableRow>
          <TableDataHeader
            sortableColumnField={'era_id'}
            fixedWidthRem={6.56}
            {...sortingProps}
          >
            <Trans t={t}>Era</Trans>
          </TableDataHeader>
          <TableDataHeader fixedWidthRem={16.875}>
            <Trans t={t}>Timestamp</Trans>
          </TableDataHeader>
          <TableDataHeader fixedWidthRem={17.5}>
            <Trans t={t}>Validator</Trans>
          </TableDataHeader>
          <TableDataHeader align="right" fixedWidthRem={10}>
            <Trans t={t}>Amount</Trans>
          </TableDataHeader>
          <TableDataHeader align="right">
            <Trans t={t}>Value</Trans> ({currency.code})
          </TableDataHeader>
          <TableDataHeader align="right">
            <Trans t={t}>Present Value</Trans> ({currency.code})
          </TableDataHeader>
        </TableRow>
      )}
      renderPaginatedData={(rewards) =>
        rewards.map((reward, idx) => (
          <AccountRewardListItem
            key={idx}
            loading={loading}
            data={reward}
            accountInfo={getAccountInfo(reward.validator_public_key)}
            currencyRate={currencyRate}
          />
        ))
      }
    />
  );
};

export default AccountRewardList;
